/**
 * Shared vocabulary for the pattern preview pipeline:
 * compile (pattern → ConstraintModel) → solve (→ SolvedChart[] | Conflict) → render.
 *
 * Variable ids:
 *   `v|${chartKey}|${seriesId}|${bar}`  series value at a bar
 *   `s|${chartKey}|${seriesId}|${bar}`  slope (angle) of a series at a bar
 *   `k|${number}`                        numeric constant node
 */

export type ChartKey = string // `${symbol}_${periodicity}_${timeframe}`
export type VarId = string
export type SeriesId = string // `${hash}.${field}` for indicators, `candle.${field}` for price

export const varId = (chartKey: ChartKey, seriesId: SeriesId, bar: number): VarId =>
  `v|${chartKey}|${seriesId}|${bar}`
export const slopeId = (chartKey: ChartKey, seriesId: SeriesId, bar: number): VarId =>
  `s|${chartKey}|${seriesId}|${bar}`
export const constId = (value: number): VarId => `k|${value}`

export interface Provenance {
  conditionId?: string
  invariant?: string
}

/** value(gt) is greater than value(lt); strict=false means greater-or-equal */
export interface OrderEdge {
  gt: VarId
  lt: VarId
  strict: boolean
  provenance: Provenance
}

export interface Equality {
  a: VarId
  b: VarId
  provenance: Provenance
}

/** |aMinuend − aSubtrahend| compared against |bMinuend − bSubtrahend| or a constant */
export interface GapConstraint {
  kind: 'converging' | 'diverging' | 'changedCmp'
  aMinuend: VarId
  aSubtrahend: VarId
  bMinuend?: VarId
  bSubtrahend?: VarId
  constant?: number
  rel?: '=' | '>' | '<'
  sameSignPairs?: boolean
  nonZeroA?: boolean
  provenance: Provenance
}

export interface ConstraintBundle {
  edges: OrderEdge[]
  equalities: Equality[]
  gaps: GapConstraint[]
}

export interface Disjunction {
  options: ConstraintBundle[]
  provenance: Provenance
  label: 'crossing' | 'lookback' | 'window' | 'previousCandle'
}

export interface CompileWarning {
  kind: 'neverFires' | 'alwaysMet' | 'unsatisfiableCount' | 'notDepicted'
  conditionId?: string
  groupId?: string
  message: string
}

export interface BadgeSpec {
  conditionId: string
  chartKey: ChartKey
  kind: 'cross' | 'up' | 'down' | 'gapBracket' | 'span' | 'window' | 'prereq' | 'guide'
  seriesId?: SeriesId
  bar?: number
  window?: { from: number; to: number }
}

export interface ResolvedOperand {
  kind: 'constant' | 'price' | 'indicator' | 'angle'
  /** constant only; Number()-coerced to mirror PDS's ToNumber comparisons */
  value?: number
  seriesId?: SeriesId
  indicatorName?: string
  field?: string
  offset: number
  lookback?: { count: number; mode: 'all' | 'some'; needed?: number }
}
